import {
	AfterViewChecked,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	HostListener,
	OnInit,
	Signal,
	TemplateRef,
	ViewChild,
	WritableSignal,
	afterEveryRender,
	computed,
	input,
	signal
} from '@angular/core';
import { PdfApi } from 'api/pdf/pdf-api.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { FoodDb } from 'lib/cosmos/food/foodDb.js';
import { getSnippetCategoryName, getSnippetTagType } from 'lib/cosmos/snippet/snippet.utils.js';
import { SnippetDb } from 'lib/cosmos/snippet/snippetDb.js';
import { LocalStorageService } from 'lib/local-storage/index.js';
import { DateTime } from 'luxon';
import { BehaviorSubject, Subject, finalize, map } from 'rxjs';
import { foodHelper } from 'src/app/modules/foods/food.helper.js';
import { P, match } from 'ts-pattern';
import { IButton } from '../buttons/index.js';
import { CoreComponent } from '../core/core.component.js';
import { ISearchInputResult } from '../form-inputs/index.js';
import { ListCategory, ListItem } from '../list/index.js';
import { ExerciseListItem } from '../list/select-exercise-list.component.js';
import { IImage } from '../ui-models.js';
import {
	DialogBuilder,
	DialogConfig,
	DialogService,
	DialogSize,
	DialogType,
	IDialog,
	IDialogCode,
	IDialogFormField,
	IDialogMessage,
	IDialogSection,
	ISectionsDialogConfig,
	SelectionDialogItem
} from './dialog.service.js';

type ResolvedSharedData = {
	readonly type: DialogType;
	readonly title: string;
	readonly size: DialogSize;
	readonly buttons: readonly IButton[];
	readonly avatar?: IImage;
	readonly sectionsConfig?: ISectionsDialogConfig;
	readonly text?: string;
	readonly message?: IDialogMessage;
	readonly code?: IDialogCode;
	readonly subtitle?: string;
};

type ExtendedFoodAmountConfig = DialogConfig.IFoodAmountDialogConfig & {
	readonly amountSignal: WritableSignal<number>;
	readonly portionIdSignal: WritableSignal<string | undefined>;
};

type ActiveSectionStorageItem = {
	readonly dialogId: string;
	readonly sectionId: string;
};

@Component({
	selector: 'lib-dialog',
	templateUrl: './dialog.component.html',
	styleUrls: ['./dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class DialogComponent extends CoreComponent implements OnInit, AfterViewChecked {
	// inputs
	public readonly dialogType = input.required<DialogType>();

	// classes
	public readonly dialogClasses = {
		dialogOpened: 'opened',
		dialogClosed: 'closed',
		dialogOverlayOpened: 'opened',
		dialogOverlayClosed: 'closed'
	};

	// dialog config
	private readonly currentDialog: WritableSignal<IDialog | undefined> = signal(undefined);

	// buttons
	public readonly footerButtons: Signal<readonly IButton[]> = computed(() => {
		const dialog = this.currentDialog();

		if (!dialog) {
			return [];
		}

		const footerbuttons: IButton[] = [];

		if (dialog.shared.footerButtons) {
			footerbuttons.push(...dialog.shared.footerButtons({ closeDialog: () => this.closeDialog() }));
		}

		if (dialog.type === 'form') {
			const formConfig = this.formConfig();

			if (formConfig?.hideSaveButton === true) {
				// save button disabled - used e.g. in form
			} else {
				// add save footer button
				footerbuttons.push({
					title: 'Save',
					buttonType: 'primary',
					buttonSize: 'large',
					disabled: () => !this.canSubmitForm(),
					click: () => {
						this.handleFormSave();
					}
				});
			}
		} else if (dialog.type === 'foodAmount') {
			const foodAmount = this.foodAmountConfig();
			if (foodAmount) {
				// add save footer button
				footerbuttons.push({
					title: 'Save',
					buttonType: 'primary',
					buttonSize: 'large',
					disabled: () => !foodAmount.foodAmount.amount,
					click: () => {
						this.handleFoodAmountSave();
					}
				});
			}
		}

		return footerbuttons;
	});

	// state
	public readonly isDialogOpened: Signal<boolean> = computed<boolean>(() => {
		if (this.dialogService.openedDialogs().find((m) => m === this.dialogType())) {
			return true;
		}
		return false;
	});

	// sections
	public readonly activeSectionId = signal<string | undefined>(undefined);

	// shared
	public readonly sharedData = signal<ResolvedSharedData | undefined>(undefined);

	// config
	public readonly textConfig = signal<DialogConfig.ITextDialogConfig | undefined>(undefined);
	public readonly selectionConfig = signal<DialogConfig.ISelectionDialogConfig<unknown> | undefined>(undefined);
	public readonly imageConfig = signal<DialogConfig.IImageDialogConfig | undefined>(undefined);
	public readonly imagesConfig = signal<DialogConfig.IImagesDialogConfig | undefined>(undefined);
	public readonly foodAmountConfig = signal<DialogConfig.IFoodAmountDialogConfig | undefined>(undefined);
	public readonly videoConfig = signal<DialogConfig.IVideoDialogConfig | undefined>(undefined);
	public readonly pdfConfig = signal<DialogConfig.IPdfDialogConfig | undefined>(undefined);
	public readonly genAiConfig = signal<DialogConfig.IGenAiDialogConfig | undefined>(undefined);
	public readonly snippetConfig = signal<DialogConfig.SnippetDialogConfig | undefined>(undefined);
	public readonly formConfig = signal<DialogConfig.IFormDialogConfig<any> | undefined>(undefined);
	public readonly exerciseConfig = signal<DialogConfig.IExerciseDialogConfig | undefined>(undefined);
	public readonly azureSearchConfig = signal<DialogConfig.IAzureSearchDialogConfig<any> | undefined>(undefined);

	public readonly pdfGenerating = signal(false);
	public readonly pdfResult = signal<PdfApi.GeneratePdfResponse | undefined>(undefined);
	public readonly selectionItems = signal<readonly ListItem<unknown>[]>([]);
	private readonly snippets = signal<readonly SnippetDb.ISnippetItem[]>([]);
	public readonly canSubmitForm = signal(false);

	private readonly contentHeight = signal<string | undefined>(undefined);
	public readonly calculatedContentHeight = computed<string | undefined>(() => {
		return this.contentHeight();
	});

	public readonly snippetListItems = computed<readonly ListItem<SnippetDb.ISnippetItem>[]>(() => {
		return this.snippets().map<ListItem<SnippetDb.ISnippetItem>>((m) => {
			return {
				data: m,
				id: m.id,
				title: m.system.name,
				subtitle: m.text,
				tag: {
					text: getSnippetCategoryName(m.category),
					tagType: getSnippetTagType(m.category)
				}
			};
		});
	});

	public readonly snippetCategories = computed<readonly ListCategory<SnippetDb.ISnippetItem>[]>(() => {
		return [
			{
				title: 'Warm up',
				filterItems: (items) => items.filter((m) => m.data.category === 'WarmupInstruction')
			},
			{
				title: 'Cooldown',
				filterItems: (items) => items.filter((m) => m.data.category === 'CooldownInstruction')
			},
			{
				title: 'Misc',
				filterItems: (items) => items.filter((m) => m.data.category === 'Misc')
			}
		];
	});

	public readonly contentTemplate = signal<TemplateRef<any> | undefined>(undefined);
	public readonly azureSearchItems = signal<ListItem<any>[]>([]);
	public readonly azureSearchItemsLoaded = signal(false);
	public readonly foodAmountNutrition = computed<FoodDb.IFoodNutrients | undefined>(() => {
		const foodAmountConfig = this.foodAmountConfig();

		if (!foodAmountConfig) {
			return;
		}
		return foodHelper.calculateFoodNutrientsForAmount(foodAmountConfig.food, foodAmountConfig.foodAmount);
	});
	public readonly foodAmountText = computed<string | undefined>(() => {
		const foodAmountConfig = this.foodAmountConfig();

		if (!foodAmountConfig) {
			return;
		}
		return foodHelper.getAmountText(foodAmountConfig.foodAmount, foodAmountConfig.food);
	});

	public readonly extendedFoodAmountConfig = computed<ExtendedFoodAmountConfig | undefined>(() => {
		const foodAmountConfig = this.foodAmountConfig();

		if (!foodAmountConfig) {
			return undefined;
		}

		const extended: ExtendedFoodAmountConfig = {
			...foodAmountConfig,
			amountSignal: signal(foodAmountConfig.foodAmount.amount),
			portionIdSignal: signal(foodAmountConfig.foodAmount.portionId)
		};

		return extended;
	});

	public readonly footerButtonLocation: Signal<'content' | 'bottom'> = computed(() => {
		return this.isMobileView() ? 'content' : 'bottom';
	});

	public readonly dialogZIndex: Signal<number> = computed(() => {
		if (!this.isDialogOpened()) {
			return -10;
		}

		let zIndex = this.dialogOverlayZIndex() + 1;

		if (this.dialogType() === 'confirmation') {
			zIndex += 100; // extra zindex for confirmation
		}
		return zIndex;
	});

	public readonly dialogOverlayZIndex: Signal<number> = computed(() => {
		if (!this.isDialogOpened()) {
			return -10;
		}

		const indexOfCurrentDialog: number = this.dialogService
			.openedDialogs()
			.findIndex((m) => m === this.dialogType());
		let zIndex = 800 + indexOfCurrentDialog;

		if (this.dialogType() === 'confirmation') {
			zIndex += 100; // extra zindex for confirmation
		}

		return zIndex;
	});

	@ViewChild('dialogOverlay', { static: true }) dialogOverlayElement!: ElementRef<HTMLElement>;
	@ViewChild('dialog', { static: true }) dialogElement!: ElementRef<HTMLElement>;

	@ViewChild('header', { static: false }) headerElement?: ElementRef<HTMLElement>;
	@ViewChild('footer', { static: false }) footerElement?: ElementRef<HTMLElement>;

	@HostListener('document:keydown.control.s', ['$event'])
	handleKeyboardtEvent(event: KeyboardEvent) {
		event.preventDefault(); // prevents default action (e.g. save document in browser)

		const sharedData = this.sharedData();

		if (!sharedData) {
			return;
		}
		if (sharedData.type === 'form') {
			this.handleFormSave();
		}

		if (sharedData.type === 'foodAmount') {
			this.handleFoodAmountSave();
		}
	}

	public readonly imagesColumnsPerRow = computed<number>(() => {
		if (this.isMobileView()) {
			return 3;
		}
		if (this.isTabletView()) {
			return 5;
		}
		return 6;
	});

	constructor(
		private readonly dialogService: DialogService,
		private readonly localStorageService: LocalStorageService
	) {
		super();

		afterEveryRender(() => {
			this.updateContentHeight();
		});
	}

	/**
	 * Using 'afterRender' doesn't seem to work properly in some cases
	 * Eg. opening edit avatar from card didn't display footer because the
	 * element wasn't properly rendered (hydrated) and height of content
	 * was calculated incorrectly.
	 * However 'afterRender' works correctly in other cases, such as opening -> closing -> opening exercise
	 * selection dialog in workout designer
	 */
	ngAfterViewChecked(): void {
		this.updateContentHeight();
	}

	ngOnInit(): void {
		if (!this.platformService.isBrowser) {
			return;
		}
		this.subscribeToDialogChange();
		this.subscribeToCloseDialogChange();
	}

	handleOverlayClick(): void {
		this.closeDialog();
	}

	handleCloseDialog(): void {
		this.closeDialog();
	}

	handleDownloadPdf(pdfUrl: string): void {
		window.open(pdfUrl);
	}

	handleAmountChanged(amount: number | undefined): void {
		const foodAmountConfig = this.foodAmountConfig();
		if (!foodAmountConfig) {
			return;
		}

		this.foodAmountConfig.update((config) => {
			if (config) {
				config.foodAmount.amount = amount ?? 0;
			}

			return super.clone(config);
		});
	}

	handleSetPortion(portionId: string | undefined): void {
		if (!portionId) {
			return;
		}

		const foodAmountConfig = this.foodAmountConfig();
		if (!foodAmountConfig) {
			return;
		}

		const currentFoodAmountPortionId = foodAmountConfig.foodAmount.portionId;

		const portion = foodAmountConfig.portionSelectionItems.find((m) => m.data.id === portionId);

		if (!portion) {
			return;
		}

		this.foodAmountConfig.update((config) => {
			if (config) {
				config.foodAmount.portionId = portionId;
				config.foodAmount.gramsPerAmount = portion.data.gramWeight;
			}

			return super.clone(config);
		});

		// set new amount based on the selected type
		// but only if changed from or to grams type
		if (
			currentFoodAmountPortionId === foodHelper.getGramsPortionCodename() ||
			portion.value === foodHelper.getGramsPortionCodename()
		) {
			if (portion.value === foodHelper.getGramsPortionCodename()) {
				// set value to be 100 as a basis of grams portion
				this.handleAmountChanged(100);
			} else {
				// otherwise set value to be 1
				this.handleAmountChanged(1);
			}
		}
	}

	handleFoodAmountSave(): void {
		const foodAmountConfig = this.foodAmountConfig();
		if (!foodAmountConfig) {
			return;
		}

		if (!foodAmountConfig.foodAmount.amount) {
			// cant' save, amount is not set or is 0
			return;
		}

		foodAmountConfig.save(foodAmountConfig.foodAmount);
		this.closeDialog();
	}

	handleAzureSeach(search: ISearchInputResult): void {
		this.searchViaAzure(search.search);
	}

	handleSelectSection(section: IDialogSection): void {
		const dialogToOpen = section.dialog(new DialogBuilder());

		const currentDialog = this.currentDialog();

		if (!currentDialog) {
			return;
		}

		const newDialog: IDialog = {
			...dialogToOpen,
			sectionsConfig: currentDialog.sectionsConfig // preserve sections config across dialogs
		};

		this.initDialog(newDialog);

		// set active section
		this.activeSectionId.set(section.id);

		const sharedData = this.sharedData();

		if (sharedData?.sectionsConfig) {
			// store section
			this.storeActiveSection(sharedData.sectionsConfig.dialogId, section.id);
		}
	}

	handleSelectAzureSearchItem(item: ListItem<any>): void {
		const azureSearch = this.azureSearchConfig();
		if (!azureSearch) {
			return;
		}

		azureSearch.select(item);
		this.closeDialog();
	}

	handleSelectionItemClick(item: ListItem<any>): void {
		const selectionConfig = this.selectionConfig();

		if (!selectionConfig) {
			return;
		}
		selectionConfig.select(item.data);

		if (!selectionConfig.enableMultiSelect) {
			// only close dialog if not multi select
			this.closeDialog();
		}
	}

	handleSnippetSelectItemClick(item: ListItem<SnippetDb.ISnippetItem>): void {
		const snippetConfig = this.snippetConfig();
		if (!snippetConfig) {
			return;
		}
		snippetConfig.select(item.data);
		this.closeDialog();
	}

	handleExerciseSelectItemClick(item: ExerciseListItem): void {
		const exerciseConfig = this.exerciseConfig();

		if (!exerciseConfig) {
			return;
		}
		exerciseConfig.select(item);
		this.closeDialog();
	}

	handleFormFieldFileDelete(
		form: DialogConfig.IFormDialogConfig<any>,
		field: IDialogFormField<any>,
		file: CosmosDb.ICosmosFileReference
	): void {
		field.fileRemove?.(file);
	}

	handleFormFieldFileSelect(
		form: DialogConfig.IFormDialogConfig<any>,
		field: IDialogFormField<any>,
		file: CosmosDb.ICosmosFileReference
	): void {
		field.fileSelected?.(file);
	}

	handleFormFieldValueChange(
		form: DialogConfig.IFormDialogConfig<any>,
		field: IDialogFormField<any>,
		value?: string | number | DateTime | CosmosDb.ICosmosFileReference[]
	): void {
		form.item[field.propertyName] = value;
		this.canSubmitForm.set(this.getCanSaveForm(form));
	}

	getFormFieldValueAsString(
		form: DialogConfig.IFormDialogConfig<any>,
		field: IDialogFormField<any>
	): WritableSignal<string | undefined> {
		return signal(form.item[field.propertyName]);
	}

	getFormFieldValueAsFileReferences(
		form: DialogConfig.IFormDialogConfig<any>,
		field: IDialogFormField<any>
	): WritableSignal<CosmosDb.ICosmosFileReference[] | undefined> {
		return signal(form.item[field.propertyName]);
	}

	getFormFieldValueAsNumber(
		form: DialogConfig.IFormDialogConfig<any>,
		field: IDialogFormField<any>
	): WritableSignal<number | undefined> {
		return signal(form.item[field.propertyName]);
	}

	getFormFieldValueAsDatetime(
		form: DialogConfig.IFormDialogConfig<any>,
		field: IDialogFormField<any>
	): WritableSignal<DateTime | undefined> {
		return signal(form.item[field.propertyName]);
	}

	handleFormSave(): void {
		if (!this.canSubmitForm()) {
			return;
		}

		const form = this.formConfig();

		if (!form) {
			return;
		}

		form.save(form.item);
		this.closeDialog();
	}

	handleSelectImage(event: Event, config: DialogConfig.IImagesDialogConfig, image: IImage): void {
		event.preventDefault();
		config.onSelect?.(image);
		this.closeDialog();
	}

	private updateContentHeight(): void {
		const headerHeightPx: number = this.headerElement?.nativeElement?.offsetHeight ?? 0;
		const footerHeightPx: number = this.footerElement?.nativeElement?.offsetHeight ?? 0;

		const newContentHeight: string = `calc(100% - ${headerHeightPx + footerHeightPx}px)`;

		if (newContentHeight !== this.contentHeight()) {
			setTimeout(() => {
				this.contentHeight.set(newContentHeight);
			}, 0);
		}
	}

	private startGeneratingPdf(): void {
		const pdfConfig = this.pdfConfig();

		if (!pdfConfig) {
			return;
		}

		this.pdfGenerating.set(true);

		super.subscribeToObservable(
			pdfConfig.generatePdf().pipe(
				map((response) => {
					this.notificationService.notify(`PDF Generated`);
					this.pdfResult.set(response);
					window.open(response.pdfUrl);
				}),
				finalize(() => {
					this.pdfGenerating.set(false);
				})
			)
		);
	}

	private searchViaAzure(search?: string): void {
		const azureSearch = this.azureSearchConfig();
		if (!azureSearch) {
			return;
		}

		super.subscribeToObservable(
			azureSearch.search({ search: search, page: 1, pageSize: azureSearch.pageSize ?? 25 }).pipe(
				map((response) => {
					this.azureSearchItems.set(azureSearch.mapSearchResponse(response));
					this.azureSearchItemsLoaded.set(true);
				})
			)
		);
	}

	private getCanSaveForm(form: DialogConfig.IFormDialogConfig<any>): boolean {
		let canSubmitForm: boolean = true;

		for (const formField of form.fields) {
			const fieldValue = form.item[formField.propertyName];
			if (formField.required && !fieldValue) {
				// field value is not set
				canSubmitForm = false;
			}
		}

		return canSubmitForm;
	}

	private subscribeToCloseDialogChange(): void {
		super.subscribeToObservable(
			this.getDialogCloseObservable().pipe(
				map(() => {
					this.closeDialog();
				})
			)
		);
	}

	private getDialogCloseObservable(): Subject<void> {
		return this.dialogService.dialogCloseEntries[this.dialogType()];
	}

	private getDialogChangeObservable(): BehaviorSubject<IDialog | undefined> {
		return this.dialogService.dialogChangeEntries[this.dialogType()];
	}

	private initDialog(dialog: IDialog): void {
		const size = match({ type: dialog.type, isMobileView: this.isMobileView() })
			.returnType<DialogSize>()
			.with({ type: 'confirmation' }, () => 'confirmation')
			.with({ isMobileView: true }, () => 'mobile')
			.otherwise(() => dialog.size);

		// shared
		this.sharedData.set({
			type: dialog.type,
			title: dialog.shared.title ?? dialog.defaultTitle,
			size: size,
			subtitle: dialog.shared.subtitle,
			avatar: dialog.shared.avatar,
			buttons:
				dialog.shared?.buttons?.({
					closeDialog: () => this.closeDialog()
				}) ?? [],
			sectionsConfig: dialog?.sectionsConfig,
			message: dialog.shared.message,
			code: dialog.shared.code,
			text: dialog.shared.text
		});

		// type configs
		this.selectionItems.set(this.mapToListItems(dialog.configs.selection?.items ?? []));
		this.selectionConfig.set(dialog.configs.selection);
		this.imageConfig.set(dialog.configs.image);
		this.imagesConfig.set(dialog.configs.images);
		this.textConfig.set(dialog.configs.text);
		this.videoConfig.set(dialog.configs.video);
		this.pdfConfig.set(dialog.configs.pdf);
		this.genAiConfig.set(dialog.configs.genAi);
		this.snippetConfig.set(dialog.configs.snippet);
		this.formConfig.set(dialog.configs.form);
		this.foodAmountConfig.set(dialog.configs.foodAmount);
		this.azureSearchConfig.set(dialog.configs.azureSearch);
		this.exerciseConfig.set(dialog.configs.exercise);

		// reset data
		this.pdfResult.set(undefined);

		this.initComponent(dialog);
	}

	private subscribeToDialogChange(): void {
		super.subscribeToObservable(
			this.getDialogChangeObservable().pipe(
				map((dialog) => {
					this.currentDialog.set(dialog);

					if (dialog) {
						this.initDialog(dialog);
					}
				})
			)
		);
	}

	private mapToListItems(selectionItems: readonly SelectionDialogItem<any>[]): readonly ListItem<any>[] {
		return selectionItems.map((m) => {
			const item: ListItem<any> = {
				...m,
				buttons: m.buttons?.({
					closeDialog: () => this.closeDialog()
				})
			};

			return item;
		});
	}

	private initComponent(dialog: IDialog): void {
		// set content template
		this.contentTemplate.set(dialog.configs?.standard?.template?.());

		if (dialog.configs.azureSearch) {
			// init default search
			this.searchViaAzure(dialog.configs.azureSearch.defaultSearchTerm ?? '');
		}

		if (dialog.configs.form) {
			this.canSubmitForm.set(this.getCanSaveForm(dialog.configs.form));
		}

		const sectionConfig = dialog.sectionsConfig;

		if (sectionConfig && dialog.type === 'sections') {
			// init section
			const lastActiveSectionForDialog = this.getActiveSectionForDialog(sectionConfig.dialogId);

			const activeSection = match(lastActiveSectionForDialog)
				.returnType<IDialogSection | undefined>()
				.with(P.nonNullable, (m) => {
					const section = sectionConfig.sections.find((section) => section.id === m.sectionId);

					if (section) {
						return section;
					}

					// no section found, use first section
					return dialog.sectionsConfig?.sections?.[0];
				})
				.otherwise(() => dialog.sectionsConfig?.sections?.[0]);

			if (activeSection) {
				this.activeSectionId.set(activeSection.id);
				this.handleSelectSection(activeSection);
			}
		}

		if (dialog.type === 'pdf' && dialog.configs.pdf) {
			// generate pdf
			this.startGeneratingPdf();
		}

		if (dialog.type === 'snippet') {
			this.loadSnippets();
		}

		this.openDialog();
	}

	private loadSnippets(): void {
		super.subscribeToObservable(
			this.httpService.listOwned<SnippetDb.ISnippetItem>('snippet').pipe(
				map((response) => {
					this.snippets.set(response.data);
				})
			)
		);
	}

	private openDialog(): void {
		if (!this.dialogElement) {
			return;
		}
		this.dialogElement.nativeElement.classList.remove(this.dialogClasses.dialogClosed);
		this.dialogElement.nativeElement.classList.add(this.dialogClasses.dialogOpened);

		this.dialogOverlayElement.nativeElement.classList.remove(this.dialogClasses.dialogOverlayClosed);
		this.dialogOverlayElement.nativeElement.classList.add(this.dialogClasses.dialogOverlayOpened);

		// set opened dialogs
		this.dialogService.openedDialogs.update((openedDialogs) => {
			return [...openedDialogs, this.dialogType()];
		});

		if (this.dialogService.openedDialogsCount() > 0) {
			// disable body scroll
			this.disableBodyScroll();
		}
	}

	private closeDialog(): void {
		if (!this.dialogElement) {
			return;
		}
		this.dialogElement.nativeElement.classList.remove(this.dialogClasses.dialogOpened);
		this.dialogElement.nativeElement.classList.add(this.dialogClasses.dialogClosed);

		this.dialogOverlayElement.nativeElement.classList.remove(this.dialogClasses.dialogOverlayOpened);
		this.dialogOverlayElement.nativeElement.classList.add(this.dialogClasses.dialogOverlayClosed);

		// set opened dialogs
		this.dialogService.openedDialogs.update((openedDialogs) => {
			return openedDialogs.filter((m) => m !== this.dialogType());
		});

		if (this.dialogService.openedDialogsCount() === 0) {
			// enable body scroll
			this.enableBodyScroll();
		}

		// unset template
		this.contentTemplate.set(undefined);

		// unset shared data
		this.sharedData.set(undefined);
	}

	private disableBodyScroll(): void {
		const htmlElement = document.getElementsByTagName('html')?.[0];

		if (htmlElement) {
			htmlElement.style.overflowY = 'hidden';
		}
	}

	private enableBodyScroll(): void {
		const htmlElement = document.getElementsByTagName('html')?.[0];

		if (htmlElement) {
			htmlElement.style.overflowY = 'auto';
		}
	}

	private getActiveSectionForDialog(dialogId: string): ActiveSectionStorageItem | undefined {
		const activeSectionItems: readonly ActiveSectionStorageItem[] =
			this.localStorageService.getObject<readonly ActiveSectionStorageItem[]>('ActiveDialogSection') ?? [];

		return activeSectionItems.find((m) => m.dialogId === dialogId);
	}

	private storeActiveSection(dialogId: string, sectionId: string): void {
		const activeSectionItems: readonly ActiveSectionStorageItem[] =
			this.localStorageService.getObject<readonly ActiveSectionStorageItem[]>('ActiveDialogSection') ?? [];

		const dialogItem = activeSectionItems.find((m) => m.dialogId === dialogId);

		if (dialogItem) {
			// update section
			this.localStorageService.setObject<readonly ActiveSectionStorageItem[]>('ActiveDialogSection', [
				...activeSectionItems.filter((m) => m.dialogId !== dialogId),
				{ dialogId: dialogId, sectionId: sectionId }
			]);
		} else {
			// new section
			this.localStorageService.setObject<readonly ActiveSectionStorageItem[]>(
				'ActiveDialogSection',
				activeSectionItems
			);
		}
	}
}
